import { Sanitized } from '../@types/sanitized';
import { ContextNames } from '../components/BoostV2/Admins/AdminToggleSwitch';
import { AgentContextHandler } from '../contexts/AgentContext';
import { CasingType, convertToCasing } from './stringHelpers';

export const currentContextParam = 'current_context';

export const COMPANY_CONTEXT = 'company-only';
export const BROKER_CONTEXT = 'broker-only';
export const OFFICE_CONTEXT = 'office-only';
export const ME_CONTEXT = 'me';

export const BOOST_ADMIN = 'boost';
export const SITE_ADMIN = 'site';
export const BROKER_ADMIN = 'broker';
export const OFFICE_ADMIN = 'office';

export const AGENT_FILTER_LEVEL = 'agent';
export const OFFICE_FILTER_LEVEL = 'office';
export const BROKER_FILTER_LEVEL = 'broker';

export type currentContextType =
    | typeof COMPANY_CONTEXT
    | typeof BROKER_CONTEXT
    | typeof OFFICE_CONTEXT
    | typeof ME_CONTEXT
    | ''
    | null;

export type AdminLevel =
    | typeof BOOST_ADMIN
    | typeof SITE_ADMIN
    | typeof BROKER_ADMIN
    | typeof OFFICE_ADMIN
    | null;

export interface ContextSwitcherNames {
    Company: string;
    Broker: string;
    Brokers: string;
    Office: string;
    Offices: string;
    'Company only': string;
    'Company & Agents': string;
    'Broker Office': string;
    'Broker & Agents': string;
    'Office Only': string;
    'Office & Agents': string;
}
export const isValidCurrentContextType = (type: null | string): boolean => {
    const typeLower = type.toLowerCase();
    return (
        typeLower === COMPANY_CONTEXT ||
        typeLower === BROKER_CONTEXT ||
        typeLower === OFFICE_CONTEXT ||
        typeLower === 'agents' /* used for viewing as specific agent? */ ||
        typeLower === 'agent' /* used for viewing as specific agent? */
    );
};

// implemented to match the backend CurrentContext::getDefaultCurrentContext
export const getDefaultCurrentContext = (agent): currentContextType => {
    if (!agent) {
        return ME_CONTEXT;
    }
    if (agent.is_site_admin ?? false) {
        return COMPANY_CONTEXT;
    }
    if (agent.is_broker_admin ?? false) {
        return BROKER_CONTEXT;
    }
    if (agent.is_office_admin ?? false) {
        return OFFICE_CONTEXT;
    }
    return ME_CONTEXT;
};

export const canEditBranding = (
    selfServiceSettings: Sanitized.BrandingSelfServiceSettings
): boolean => {
    return (
        selfServiceSettings?.dashboardLogoEnabled ||
        selfServiceSettings?.facebookPageEnabled ||
        selfServiceSettings?.displayAdLogoEnabled ||
        selfServiceSettings?.dashboardColorsEnabled
    );
};

export const mapCustomOverrides = (contextSwitcherNames: ContextSwitcherNames): ContextNames[] => {
    return Object.entries(contextSwitcherNames).map((value) => ({
        type: value[0],
        override: value[1],
    }));
};

export const getAdminLevel = (agent: Partial<AgentContextHandler>): AdminLevel => {
    if (agent?.is_site_admin) return SITE_ADMIN;
    if (agent?.is_broker_admin) return BROKER_ADMIN;
    if (agent?.is_office_admin) return OFFICE_ADMIN;
    if (agent?.is_boost_admin) return BOOST_ADMIN;
    return null;
};

export const customNames = (
    contextNames: ContextNames[],
    levels: string[],
    usePlural: boolean = false
): ContextNames[] => {
    const pluralizeAgent = (name: string): string => {
        if (name === 'agent') return 'agents';
        return name;
    };
    return levels.map((level) => {
        const pluralizeLevel = usePlural ? `${level.toLowerCase()}s` : level.toLowerCase();
        const match = contextNames?.find(({ type }) => type.toLowerCase() === pluralizeLevel);
        if (match) {
            return {
                type: level.toLowerCase(),
                override: match.override.toLowerCase(),
            };
        }
        return { type: level, override: usePlural ? pluralizeAgent(level) : level };
    });
};

export const getCustomLabel = (customContextNames: ContextNames[], label: string): string => {
    const customLabel = customNames(customContextNames, [label]);
    if (customLabel.length > 0)
        return convertToCasing(customLabel[0].override, CasingType.CASING_SENTENCE);
    return label;
};

export const getContextDataLabel = (
    customContextNames: ContextNames[],
    adminLevel: AdminLevel
): string => {
    if (adminLevel === SITE_ADMIN)
        return customContextNames?.find(({ type }) => type === 'Company').override;
    if (adminLevel === BROKER_ADMIN)
        return customContextNames?.find(({ type }) => type === 'Broker').override;
    if (adminLevel === OFFICE_ADMIN)
        return customContextNames?.find(({ type }) => type === 'Office').override;

    return 'brokerage';
};

export const getOriginalCustomNames = (
    customContextNames: ContextNames[],
    label: string
): string | undefined => {
    return customContextNames.find((c) => c.override.toLowerCase() === label.toLowerCase())?.type;
};
